/**
 * D-15: the suggested-prompt chips, as FIXED template sets selected by route type.
 *
 * Zero LLM cost, which is the entire design: generating three chips per page load would put a model
 * call in front of every visitor who never opens the panel. Rendered ONLY while the thread is empty;
 * once any turn exists they are gone and do not come back.
 *
 * THE PATHNAME NEVER REACHES THE RENDER. `resolveChipRouteType` maps a URL onto one of three route
 * types and the route type selects a constant array (T-38-81). No substring of the pathname is
 * interpolated into a chip, so a crafted URL can choose WHICH fixed set renders and nothing else.
 *
 * THIS MODULE OWNS THE ROUTE RULE. `ChatContext.resolveRouteType` delegates here rather than
 * restating the three branches, because two implementations of one routing rule is how the chips
 * and the page context sent to the server drift apart on the day a route is added.
 *
 * Zero imports, on purpose: this is the module the no-dash suite loads under vitest's node
 * environment, and it is also why `ChatContext` can import it without a cycle.
 */

/** The three route types, as the `as const` + `keyof typeof` idiom (CLAUDE.md's enum rule). */
export const chatRouteTypeMap = {
  store: 'store',
  category: 'category',
  generic: 'generic',
} as const;

export type ChatChipRouteType = keyof typeof chatRouteTypeMap;

/**
 * `/store/*` is a store, `/category/*` and `/categories` are a category, everything else is generic.
 *
 * Defensive about a non-string input because it is fed from `usePageContext().urlPathname`, which is
 * typed but arrives from the router rather than from this codebase.
 */
export const resolveChipRouteType = (pathname: string): ChatChipRouteType => {
  const path = typeof pathname === 'string' ? pathname : '';
  if (path.startsWith('/store/')) return chatRouteTypeMap.store;
  if (path.startsWith('/category/') || path.startsWith('/categories')) return chatRouteTypeMap.category;
  return chatRouteTypeMap.generic;
};

/**
 * The ten stable chip ids.
 *
 * PostHog carries the ID, never the chip's Arabic text (`chat_suggestion_clicked` takes `chip_id`
 * and `route_type` and has no parameter capable of holding prose). An id also survives a copy edit:
 * rewording a chip must not break the funnel it is measured by.
 */
export const chatChipIdMap = {
  store_reviews: 'store_reviews',
  store_trusted: 'store_trusted',
  store_similar: 'store_similar',
  store_delivery: 'store_delivery',
  category_best: 'category_best',
  category_reviewed: 'category_reviewed',
  category_cheap: 'category_cheap',
  generic_find: 'generic_find',
  generic_best: 'generic_best',
  generic_add: 'generic_add',
} as const;

export type ChatChipId = keyof typeof chatChipIdMap;

export type ChatSuggestion = {
  id: ChatChipId;
  /** Sent verbatim as the visitor's message when the chip is tapped. */
  text: string;
};

/**
 * Transcribed from `38-UI-SPEC.md` § "Suggested prompt chips (D-02, D-15)", verbatim and binding.
 *
 * The `store` and `category` sets are DEICTIC on purpose ("ده", "هنا"): they are the cheapest
 * possible demonstration that D-13's page context actually reaches the model. The `generic` set's
 * third chip deliberately routes into the long-running scrape path, which exercises WEB-06's ack,
 * completion and unread dot in a single tap.
 *
 * A `Record` keyed by the route-type map, so a fourth route type could not be added without its
 * chips.
 */
export const SUGGESTIONS_BY_ROUTE_TYPE: Record<ChatChipRouteType, readonly ChatSuggestion[]> = {
  store: [
    { id: 'store_reviews', text: 'الناس بتقول إيه عن المتجر ده؟' },
    { id: 'store_trusted', text: 'المتجر ده موثوق؟' },
    { id: 'store_similar', text: 'فيه متاجر شبهه أحسن؟' },
    { id: 'store_delivery', text: 'بيوصّل لمحافظتي؟' },
  ],
  category: [
    { id: 'category_best', text: 'إيه أحسن المتاجر في القسم ده؟' },
    { id: 'category_reviewed', text: 'مين اللي عنده ريفيوهات كتير هنا؟' },
    { id: 'category_cheap', text: 'عايز حاجة رخيصة من القسم ده.' },
  ],
  generic: [
    { id: 'generic_find', text: 'دورلي على متجر بالاسم.' },
    { id: 'generic_best', text: 'إيه أحسن متاجر العطور في مصر؟' },
    { id: 'generic_add', text: 'عايز أضيف متجر على يتفرج.' },
  ],
};

/** The chip set for a route type. Total: an unrecognised value falls back to the generic set. */
export const suggestionsForRouteType = (routeType: string): readonly ChatSuggestion[] => {
  if (typeof routeType === 'string' && routeType in SUGGESTIONS_BY_ROUTE_TYPE) {
    return SUGGESTIONS_BY_ROUTE_TYPE[routeType as ChatChipRouteType];
  }
  return SUGGESTIONS_BY_ROUTE_TYPE.generic;
};
